
import React, { useState, useEffect } from 'react';
import StorefrontLayout from './components/StorefrontLayout';
import AdminLayout from './components/AdminLayout';
import StoreHome from './pages/StoreHome';
import ProductDetail from './pages/ProductDetail';
import StoreProfile from './pages/StoreProfile';
import CartPage from './pages/CartPage';
import OrderSuccess from './pages/OrderSuccess';
import CustomerOrders from './pages/CustomerOrders';
import CustomerWishlist from './pages/CustomerWishlist';
import CustomerSubscriptions from './pages/CustomerSubscriptions';
import AdminDashboard from './pages/AdminDashboard';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminProducts from './pages/AdminProducts';
import AdminCategories from './pages/AdminCategories';
import AdminOrders from './pages/AdminOrders';
import AdminSettings from './pages/AdminSettings';
import AdminMessages from './pages/AdminMessages';
import AdminFinances from './pages/AdminFinances';
import AdminBilling from './pages/AdminBilling';
import AdminInvoices from './pages/AdminInvoices';
import AdminShipping from './pages/AdminShipping';
import AdminTeam from './pages/AdminTeam';
import AdminFlashSales from './pages/AdminFlashSales';
import AdminCoupons from './pages/AdminCoupons';
import AdminSubBoxes from './pages/AdminSubBoxes';
import AdminInfluencers from './pages/AdminInfluencers';
import AdminVIP from './pages/AdminVIP';
import AdminLandingPages from './pages/AdminLandingPages';
import AdminBundles from './pages/AdminBundles';
import AdminSocialHub from './pages/AdminSocialHub';
import AdminDynamicPricing from './pages/AdminDynamicPricing';
import CampaignLandingPage from './pages/CampaignLandingPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import LoginPage from './pages/LoginPage';
import SellerSignup from './pages/SellerSignup';
import { authService } from './services/authService';
import { orderService } from './services/orderService';
import { Product, CartItem, User, UserRole, Store, ProductVariant } from './types';
import { Loader2 } from 'lucide-react';
import { DEFAULT_STORE_ID } from './constants';
import { Language, translations } from './lib/i18n';
import { StoreProvider, useStoreContext } from './contexts/StoreContext';
import InfluencerBadge from './components/InfluencerBadge';

type Page = 'home' | 'cart' | 'admin' | 'super-admin' | 'analytics' | 'products' | 'categories' | 'orders' | 'settings' | 'login' | 'product-detail' | 'order-success' | 'my-orders' | 'seller-signup' | 'store-profile' | 'messages' | 'finances' | 'billing' | 'taxation' | 'shipping' | 'team' | 'flash-sales' | 'wishlist' | 'coupons' | 'sub-boxes' | 'subscriptions' | 'influencers' | 'vip-club' | 'lp-architect' | 'lp-view' | 'bundles' | 'social-commerce' | 'ai-pricing';

const AppContent: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [lastOrderId, setLastOrderId] = useState<string | null>(null);
  const [lpSlug, setLpSlug] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [lang, setLang] = useState<Language>('en');
  const [refId, setRefId] = useState<string | null>(null);

  const { isLoading: isStoreLoading } = useStoreContext();

  useEffect(() => {
    // Detect Referral ID from URL
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setRefId(ref);

    // Detect Landing Page Path
    const path = window.location.pathname;
    if (path.startsWith('/lp/')) {
       const slug = path.split('/')[2];
       setLpSlug(slug);
       setCurrentPage('lp-view');
    }

    const { data: { subscription } } = authService.onAuthStateChange((updatedUser) => {
      setUser(updatedUser);
      setIsInitializing(false);
      if (!updatedUser && isAdminPage(currentPage)) {
        setCurrentPage('home');
      }
    });

    return () => subscription.unsubscribe();
  }, [currentPage]);

  const isAdminPage = (page: Page) => 
    ['admin', 'super-admin', 'analytics', 'products', 'categories', 'orders', 'settings', 'messages', 'finances', 'billing', 'taxation', 'shipping', 'team', 'flash-sales', 'coupons', 'sub-boxes', 'influencers', 'vip-club', 'lp-architect', 'bundles', 'social-commerce', 'ai-pricing'].includes(page);

  const handleAddToCart = (product: Product, variant?: ProductVariant) => {
    setCart(prev => {
      const existing = prev.find(item => 
        item.productId === product.id && 
        (!variant || item.variantId === variant.id)
      );
      
      if (existing) {
        return prev.map(item => 
          (item.productId === product.id && (!variant || item.variantId === variant.id))
          ? { ...item, quantity: item.quantity + 1 } 
          : item
        );
      }
      
      return [...prev, { 
        productId: product.id, 
        variantId: variant?.id,
        quantity: 1, 
        product,
        variant: variant
      }];
    });
  };

  const handleUpdateCartQuantity = (productId: string, delta: number) => {
    setCart(prev => prev.map(item => 
      item.productId === productId 
      ? { ...item, quantity: Math.max(1, item.quantity + delta) } 
      : item
    ));
  };

  const handleRemoveFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.productId !== productId));
  };

  const handleCheckout = async (paymentMethod: 'CARD' | 'UPI' | 'COINS', coinsToRedeem: number = 0, shippingMethodId: string, couponId?: string) => {
    if (cart.length === 0) return;
    const targetStoreId = cart[0].product.storeId || DEFAULT_STORE_ID;
    
    // Check for influencer attribution
    const attributionId = sessionStorage.getItem('lynqar_attribution_id') || refId || undefined;

    const res = await (orderService as any).createOrder(
      targetStoreId,
      cart,
      user?.id || 'guest-session',
      paymentMethod,
      coinsToRedeem,
      shippingMethodId,
      couponId,
      attributionId
    );

    if (res.success) {
      setLastOrderId(res.orderId || '');
      setCart([]);
      setCurrentPage('order-success');
    }
  };

  const handleNavigate = (page: Page, params?: any) => {
    if (page === 'product-detail' && params) {
      setSelectedProduct(params);
    }
    if (page === 'store-profile' && params) {
      setSelectedStore(params);
    }
    
    if (isAdminPage(page)) {
      if (!user || ![UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.SELLER, UserRole.STAFF].includes(user.role)) {
        setCurrentPage('login');
        return;
      }
      if (user.role === UserRole.SUPER_ADMIN && page === 'admin') {
        setCurrentPage('super-admin');
        return;
      }
    }
    setCurrentPage(page);
  };

  const handleLogout = async () => {
    await authService.signOut();
    setCurrentPage('home');
  };

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  if (isInitializing || isStoreLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-500 font-medium animate-pulse tracking-widest uppercase text-[10px]">Lynqar Engine Booting...</p>
      </div>
    );
  }

  const renderContent = () => {
    if (currentPage === 'login') {
      return <LoginPage onLoginSuccess={() => handleNavigate(user?.role === UserRole.SUPER_ADMIN ? 'super-admin' : 'admin')} />;
    }
    if (currentPage === 'seller-signup') {
      return <SellerSignup onSignupSuccess={() => setCurrentPage('admin')} />;
    }

    const activeStoreId = user?.storeId || DEFAULT_STORE_ID;

    switch (currentPage) {
      case 'lp-view':
        return <CampaignLandingPage slug={lpSlug || ''} onAddToCart={handleAddToCart} onViewProduct={(p) => handleNavigate('product-detail', p)} />;
      case 'home':
        return (
          <StoreHome 
            onAddToCart={handleAddToCart} 
            onViewProduct={(p) => handleNavigate('product-detail', p)} 
            onViewStore={(s) => handleNavigate('store-profile', s)}
            onBecomeSeller={() => setCurrentPage('seller-signup')} 
            lang={lang}
          />
        );
      case 'store-profile':
        return selectedStore ? (
          <StoreProfile 
            store={selectedStore} 
            onAddToCart={handleAddToCart} 
            onViewProduct={(p) => handleNavigate('product-detail', p)} 
            onBack={() => setCurrentPage('home')} 
          />
        ) : null;
      case 'product-detail':
        return selectedProduct ? (
          <ProductDetail product={selectedProduct} onAddToCart={handleAddToCart} onBack={() => setCurrentPage('home')} />
        ) : null;
      case 'cart':
        return (
          <CartPage 
            items={cart}
            user={user}
            onUpdateQuantity={handleUpdateCartQuantity}
            onRemoveItem={handleRemoveFromCart}
            onCheckout={handleCheckout}
            onNavigateHome={() => setCurrentPage('home')}
          />
        );
      case 'order-success':
        return <OrderSuccess orderId={lastOrderId || ''} onNavigateHome={() => setCurrentPage('home')} onNavigateOrders={() => setCurrentPage('my-orders')} />;
      case 'my-orders':
        return <CustomerOrders user={user} onNavigateHome={() => setCurrentPage('home')} />;
      case 'wishlist':
        return <CustomerWishlist user={user} onNavigateHome={() => setCurrentPage('home')} onAddToCart={handleAddToCart} onViewProduct={(p) => handleNavigate('product-detail', p)} />;
      case 'subscriptions':
        return <CustomerSubscriptions user={user} onNavigateHome={() => setCurrentPage('home')} />;
      case 'super-admin': return <SuperAdminDashboard />;
      case 'admin': return <AdminDashboard storeId={activeStoreId} lang={lang} />;
      case 'analytics': return <AdminAnalytics storeId={activeStoreId} />;
      case 'products': return <AdminProducts storeId={activeStoreId} lang={lang} />;
      case 'categories': return <AdminCategories storeId={activeStoreId} />;
      case 'orders': return <AdminOrders storeId={activeStoreId} />;
      case 'messages': return <AdminMessages storeId={activeStoreId} />;
      case 'finances': return <AdminFinances storeId={activeStoreId} />;
      case 'billing': return <AdminBilling storeId={activeStoreId} />;
      case 'taxation': return <AdminInvoices storeId={activeStoreId} />;
      case 'shipping': return <AdminShipping storeId={activeStoreId} />;
      case 'team': return <AdminTeam storeId={activeStoreId} />;
      case 'flash-sales': return <AdminFlashSales storeId={activeStoreId} />;
      case 'coupons': return <AdminCoupons storeId={activeStoreId} />;
      case 'sub-boxes': return <AdminSubBoxes storeId={activeStoreId} />;
      case 'influencers': return <AdminInfluencers storeId={activeStoreId} />;
      case 'vip-club': return <AdminVIP storeId={activeStoreId} />;
      case 'lp-architect': return <AdminLandingPages storeId={activeStoreId} />;
      case 'bundles': return <AdminBundles storeId={activeStoreId} />;
      case 'social-commerce': return <AdminSocialHub storeId={activeStoreId} />;
      case 'ai-pricing': return <AdminDynamicPricing storeId={activeStoreId} />;
      case 'settings': return <AdminSettings />;
      default: return <StoreHome onAddToCart={handleAddToCart} onViewProduct={(p) => handleNavigate('product-detail', p)} onViewStore={(s) => handleNavigate('store-profile', s)} onBecomeSeller={() => setCurrentPage('seller-signup')} lang={lang} />;
    }
  };

  const onAdminPage = isAdminPage(currentPage);
  const isLpView = currentPage === 'lp-view';

  return (
    <>
      {refId && <InfluencerBadge influencerId={refId} />}
      {['login', 'seller-signup'].includes(currentPage) || isLpView ? (
        renderContent()
      ) : onAdminPage ? (
        <AdminLayout user={user} activeTab={currentPage} onNavigate={handleNavigate} lang={lang} onLangChange={setLang}>
          {renderContent()}
        </AdminLayout>
      ) : (
        <StorefrontLayout cartCount={cartCount} onNavigate={handleNavigate} user={user} onLogout={handleLogout} lang={lang} onLangChange={setLang}>
          {renderContent()}
        </StorefrontLayout>
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <StoreProvider>
      <AppContent />
    </StoreProvider>
  );
};

export default App;
